import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  Grid,
  TableCell,
  TableRow,
  useTheme,
} from "@mui/material";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import { ComplianceTable } from "~/components/Completion/ComplianceTable";
import { CompletionBar } from "~/components/Completion/CompletionBar";
import { FormatRelativeDate, FormatTime } from "~/lib/date";
import {
  ComplianceAssetOrderField,
  ExceptionMutationAction,
  Maybe,
  OrderDirection,
  useGetComplianceFrameworkAssetsQuery,
} from "~/operations";
import { Loading, LoadingFailed } from "~/components/loading";
import { useSearch } from "~/components/search/useSearch";
import { GroupingButton } from "~/components/grouping-button";
import { useGroupingButton } from "~/components/grouping-button/useGroupingButton";
import { INITIAL_PAGE_RANGE, Pagination } from "~/components/pagination";
import { EmptyState } from "~/components/empty-state/empty-state";
import { useFrameworkContext } from "~/pages/compliance/Framework/Framework";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useSelection } from "~/hooks/useSelection";

import { SelectedEntityWithException } from "~/components/exceptions/types";
import { Header } from "~/types/table";

export type FrameworkAssetsProps = {};

export function FrameworkAssets({}: FrameworkAssetsProps) {
  const { space, framework } = useFrameworkContext();
  const search = useSearch();
  const [searchParams, _setSearchParams] = useSearchParams();
  const { toggleSelectedGroupingButton, selectedGroupingButton } =
    useGroupingButton();
  const [pageItems, setPageItems] = useState(INITIAL_PAGE_RANGE);
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    data: { complianceFramework } = {},
    error,
    loading,
    fetchMore,
  } = useGetComplianceFrameworkAssetsQuery({
    variables: {
      input: {
        frameworkMrn: framework.mrn,
        scopeMrn: space.mrn,
      },
      assetsInput: {
        orderBy: {
          field:
            (searchParams.get("field") as ComplianceAssetOrderField) ||
            ComplianceAssetOrderField.Name,
          direction:
            (searchParams.get("direction") as OrderDirection) ||
            OrderDirection.Asc,
        },
        query: search.searchFilters.join(" "),
        groups: selectedGroupingButton
          ? [{ groupType: selectedGroupingButton }]
          : undefined,
      },
    },
  });

  const { assets, assetGroupStatistics } = complianceFramework || {};

  const {
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    selectedEntities: selectedAssets,
    handleNodeClick,
    handleNodeChange,
  } = useSelection<SelectedEntityWithException>();

  const assetsWithExceptions = (assets?.edges || []).map((asset) => ({
    ...asset,
    node: {
      ...asset.node,
      exception: null as Maybe<{
        id: string;
        justification?: Maybe<string>;
        action: ExceptionMutationAction;
      }>,
    },
  }));

  const targetPaginatedGroup = assetsWithExceptions
    .slice(pageItems.from, pageItems.to)
    .map((edge) => ({
      mrn: edge.node.asset?.mrn as string,
      exception: edge.node.exception,
      groupId: edge.node?.exception?.id,
    }));

  const isExceptionsEnabled = false;

  const tableHeaders: Header[] = [
    isExceptionsEnabled
      ? {
          id: "SELECT",
          label: (
            <Checkbox
              checked={isGroupChecked(targetPaginatedGroup)}
              indeterminate={isGroupIndeterminate(targetPaginatedGroup)}
              onChange={(event) =>
                onGroupCheckChange(event, targetPaginatedGroup)
              }
            />
          ),
          sortable: false,
        }
      : undefined,
    {
      id: "NAME",
      label: "Asset name",
    },
    {
      id: "PLATFORM",
      label: "Platform",
    },
    {
      id: "COMPLETION",
      label: "Completion",
    },
    {
      id: "LAST_UPDATED",
      label: "Last updated",
    },
  ].flatMap((h) => h ?? []);

  setDocumentTitle(["Assets", framework.name, "Compliance", space.name]);

  if (loading && !assets) {
    return <Loading what="assets" />;
  }

  if (error) {
    return <LoadingFailed what="assets" />;
  }

  if (!assets) {
    return <EmptyState contentType="framework-assets" scope={space} />;
  }

  const handleAssetClick = (assetId: string | undefined) => {
    if (!assetId) return;

    navigate(`/space/inventory/${assetId}/overview?spaceId=${space.id}`);
  };

  const groups = assetGroupStatistics?.filter((group) => group.count > 0) || [];

  return (
    <Box>
      {groups.length > 0 && (
        <Grid container spacing={2} sx={{ my: 5 }}>
          {groups.map((group) => {
            return (
              <Grid
                key={group.groupType}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <GroupingButton
                  {...{ group }}
                  onClick={() => toggleSelectedGroupingButton(group.groupType)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
      <FilterBar
        searchId="compliance-framework-assets"
        placeholder="compliance_assets"
        search={search}
      />
      <ComplianceTable
        tableHeaders={tableHeaders}
        defaultActiveColumn={ComplianceAssetOrderField.Name}
      >
        {assetsWithExceptions
          ?.filter((x) => x.node !== null)
          .slice(pageItems.from, pageItems.to)
          .map((edge) => {
            const asset = edge.node?.asset;
            const completion = edge.node?.compliancePercentage || 0;

            return (
              <TableRow
                onClick={() => handleAssetClick(asset?.id)}
                key={asset?.id}
                sx={{
                  "& .MuiTableCell-root": { fontWeight: 400 },
                }}
              >
                {isExceptionsEnabled && (
                  <TableCell>
                    <Checkbox
                      onClick={(e) => handleNodeClick(e)}
                      onChange={(e, changed) =>
                        handleNodeChange(e, changed, {
                          mrn: asset?.mrn as string,
                          exception: edge?.node?.exception,
                        })
                      }
                      checked={Boolean(
                        selectedAssets.find(
                          (selectedCheck) => asset?.mrn === selectedCheck.mrn,
                        ),
                      )}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ width: "40%" }}>{asset?.name}</TableCell>
                <TableCell sx={{ width: "20%" }}>
                  {asset?.platform?.name}
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <CompletionBar
                    {...{
                      percent: completion,
                      height: theme.spacing(1),
                      tooltip: `Completion: ${completion}%`,
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: (theme) => theme.spacing(1.25),
                    width: "15%",
                  }}
                >
                  {FormatRelativeDate(String(asset?.updatedAt)) +
                    " - " +
                    FormatTime(String(asset?.updatedAt))}
                </TableCell>
              </TableRow>
            );
          })}
      </ComplianceTable>
      <Pagination
        fetchMore={fetchMore}
        pageInfo={assets.pageInfo}
        setPageItems={setPageItems}
        totalCount={assets.totalCount}
      />
    </Box>
  );
}
